<template>
    <li class="category__item" :class="{ 'category__item--small': small }">
        <nuxt-link
            class="category__link"
            :to="subcategoryUrl"
            @click="preloadCategoryData"
            @mouseenter="startHoverTimer"
            @mouseleave="clearHoverTimer"
        >
            <div
                :class="moreCategoriesItem ? 'category__more-categories' : 'category__h2'"
                :title="subcategory.name || (subcategory as MenuItem).title"
            >
                <span class="category__title">
                    {{ subcategory.name || (subcategory as MenuItem).title }}
                    <span v-if="subcategoryCount !== 0" class="category__count"> ({{ $numberFormat(subcategoryCount) }}) </span>
                </span>
            </div>

            <div class="category__image-container" :class="moreCategoriesItem ? 'category__more-image-container' : ''">
                <div class="category__image">
                    <img
                        :src="subcategory.image || '/nuxt-assets/images/subCategoriesMissing/Stomatologie.svg'"
                        :alt="(subcategory as CategoryTile).imageAlt || subcategory.name || (subcategory as MenuItem).title"
                        class="category__img"
                        data-not-lazy
                    />
                </div>
            </div>
        </nuxt-link>
    </li>
</template>

<script lang="ts" setup>
import { PropType } from 'vue'
import MenuItem from '~/types/MenuItem'
import CategoryTile from '~/models/Category/CategoryTile'
import useProductListPageAsyncData from '~/composables/productList/useProductListPageAsyncData'
import useHoverPreload from '~/composables/useHoverPreload'

const props = defineProps({
    subcategory: {
        type: Object as PropType<MenuItem | CategoryTile>,
        required: true,
    },

    moreCategoriesItem: {
        type: Boolean,
        default: false,
    },

    small: {
        type: Boolean,
        default: false,
    },
})

const subcategoryUrl = computed((): string => {
    if ('shopUrlPath' in props.subcategory && props.subcategory.shopUrlPath) {
        return props.subcategory.shopUrlPath
    }

    if ('url' in props.subcategory && props.subcategory.url) {
        return props.subcategory.url
    }

    return ''
})

const subcategoryCount = computed((): number => {
    if ('productsCount' in props.subcategory && props.subcategory.productsCount) {
        return props.subcategory.productsCount
    }

    if ('count' in props.subcategory && props.subcategory.count) {
        return props.subcategory.count
    }

    return 0
})

const categoryPreloaded = ref(false)
const nuxtApp = useNuxtApp()
const preloadCategoryData = () => {
    if (categoryPreloaded.value) {
        return
    }

    categoryPreloaded.value = true

    if (!('type' in props.subcategory) || !('objectId' in props.subcategory)) {
        return
    }

    const { type, objectId } = props.subcategory
    if (type !== 'category' || !objectId) {
        return
    }

    // @ts-ignore
    nuxtApp.$prefetchUrl({ fullPath: subcategoryUrl.value }, {})
    useProductListPageAsyncData({
        needsFiltersAndRichContent: true,
        inputPageFrom: null,
        inputPageTo: null,
        id: props.subcategory.objectId,
        type,
        url: subcategoryUrl.value,
    })
}

const { startHoverTimer, clearHoverTimer } = useHoverPreload(preloadCategoryData, 2000)
</script>

<style lang="scss" scoped>
.category {
    &__more-categories {
        text-decoration: underline;
        color: $color-cta;
        font-size: 16px;
        min-height: 33px;
        display: flex;
        align-items: center;
        font-weight: 500;
    }

    &__h2 {
        font-size: 16px;
        min-height: 33px;
        display: flex;
        align-items: center;
        font-weight: 500;
        color: $color-dark;
    }

    &__item {
        position: relative;
        list-style-type: none;
        flex: 1 0 50%;

        &:before {
            content: '';
            display: table;
            padding-top: 100%;
        }

        height: 64px;
        @include screen-md {
            height: 90px;
        }
    }

    &__image-container {
        box-shadow: 0 0 19px -8px rgba(0, 0, 0, 0.33);
        border-radius: 10px;
        margin-left: auto;
        align-content: center;
        width: 48px;
        height: 48px;

        @include screen-lg {
            width: 60px;
            height: 60px;
            flex: 0 0 60px;
        }
    }

    &__more-image-container {
        border-radius: 100px;
    }

    &__image {
        display: flex;
        align-items: center;
        flex: 1;
        width: 100%;
        justify-content: center;
        padding: 5px;
    }

    &__img {
        align-self: center;
        max-height: 40px;
        @include screen-lg {
            max-height: 55px;
        }
    }

    &__count {
        color: #424242;
        font-size: 14px;
        font-weight: normal;
    }

    &__link {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: grid;
        grid-template-columns: auto 48px;
        align-items: center;
        text-decoration: none;
        background: #fff;
        outline: 1px solid $color-grey-dark;
        padding: 8px 16px;

        @include screen-lg {
            padding: 15px 16px;
            grid-template-columns: auto 60px;
        }

        &:hover {
            text-decoration: none;

            .category__h2 {
                color: $color-cta;
            }
        }
    }

    &__item--small {
        height: 64px;
        @include screen-md {
            height: 60px;
        }

        .category__link {
            padding: 3px 15px;
        }

        .category__image-container {
            width: 40px;
            height: 40px;
        }

        .category__img {
            align-self: center;
            max-height: 40px;
            @include screen-lg {
                max-height: 35px;
            }
        }
    }
}
</style>
